/**
 * Builder > Triggers > And/or selector
 * <ua-builder-items-triggers-logic>
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { customElement, state } from 'lit/decorators.js';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'ua-builder-items-triggers-logic' )
export class TriggersAndOrSelector extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Update the component every time the logic changes
	@state() accessor logic = UncannyAutomator._recipe.triggers.logic;

	@state() accessor isSavingData = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		if ( UncannyAutomator._recipe.triggers.items.length >= 2 ) {
			return this.templateMultipleTriggers;
		}

		return this.templateDefault;
	}

	/**
	 * Renders the template used when there are no triggers or there is only one
	 *
	 * @return {TemplateResult} The template
	 */
	get templateDefault() {
		return html`
			<div class="header">
				<div class="header-title">${ UncannyAutomator._core.i18n.trigger.singularName }</div>
			</div>
		`;
	}

	/**
	 * Renders the template used when there are multiple triggers
	 *
	 * @return {TemplateResult} The template
	 */
	get templateMultipleTriggers() {
		// Create the dropdown
		const $dropdown = html`
			<uo-select
				@change=${ ( event ) => {
					this.logic = event.target.value;

					// Save the data
					this.saveData();
				} }
			>
				${ repeat(
					Object.entries( this.options ),
					// eslint-disable-next-line no-unused-vars
					( [ value, text ] ) => value,
					( [ value, text ] ) => html`
						<uo-select-option
							value="${ value }"
							text="${ text }"
							?selected=${ this.logic === value }
						></uo-select-option> 
					`
				) }
			</uo-select>
		`;

		return html`
			<div class="header">
				<div class="header-title">${ UncannyAutomator._core.i18n.trigger.name }</div>
				<div class="header-logic" dir="auto">
					<span class="header-logic-title">
						${ UncannyAutomator._core.i18n.trigger.logic.runWhen }
					</span>

					<span class="header-logic-dropdown">
						${ $dropdown }
					</span>

					<span class="header-logic-description">
						${ this.logic === 'any' ? UncannyAutomator._core.i18n.trigger.logic.runWhenAny : UncannyAutomator._core.i18n.trigger.logic.runWhenAll }
					</span>

					${ this.isSavingData ? html`<span class="header-loading"></span>` : '' }
				</div>
			</div>
		`;
	}

	/**
	 * Performs a REST call to save the selected
	 */
	saveData() {
		// Start loading animation
		this.isSavingData = true;

		UncannyAutomator._core.recipe.item.setTriggerLogic(
			this.logic
		).then( response => {
			this.isSavingData = false;
		} ).catch( error => {
			this.isSavingData = false;
		} );
	}

	/**
	 * Returns the options for the dropdown
	 *
	 * @return {Object} where the keys are the option values
	 */
	get options() {
		return {
			any: UncannyAutomator._core.i18n.trigger.logic.any,
			all: UncannyAutomator._core.i18n.trigger.logic.all,
		}
	}
}
